import React, { useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import PropTypesImmutable from 'react-immutable-proptypes';
import { connect } from 'react-redux';
import { getLoadingSelector } from '../../utils/asyncHelpers';
import {
  SearchLegalEntityConsumers,
  CancelingCertificatesGoAmount,
  CancelingAvailabilityWarning,
} from '../../components';
import { Spin, Button, Row, Col, Form, Layout } from 'antd';
import { goToConsumptionPoint } from '../../utils/gotoLink';
import { Select } from '../../atoms';
import * as actions from '../../actions/actionCreators/cancelingCertificatesActionCreators';
import * as selectors from '../../reducers/cancelingCertificatesReducer';
import { meteringPointFields } from '../../constants/meteringPoint';
import classifiersStore, {
  mapPropertiesToObject,
} from '../../utils/classifiersStore';
import { classifierConsumptionUsageProperties } from '../../constants/classifier';
import { useTranslation } from 'react-i18next';
import FuelType from '../../constants/classificators/fuelType';
import { getEecsGoRowsForAction } from '../../reducers/accountOverviewReducer';
import { CancelEecsGOsTable } from '../../components/PerformCertificatesTable/CancelEecsGOsTable';

const FormItem = Form.Item;

const ALL_TRANSPORT = 'ALL_TRANSPORT';
const ALL = 'ALL';

const CancelingCertificatesEecs = ({
  pointId,
  consumerId,
  points,
  isPointsLoading,
  goAmounts,
  isGoLoading,
  isCancellingCertificates,
  selectPeriods,
  periods,
  isCertificatesLoading,
  accountId,
  cancelCertificates,
  goAmountTotal,
  point,
  selectConsumer,
  selectPoint,
  fetchConsumerPoints,
  fetchGoAmount,
  reset,
  eecsGoRows,
}) => {
  const { t } = useTranslation('cancelingCertificates');

  useEffect(() => {
    reset();
  }, [reset]);

  const handleSelectConsumer = useCallback(
    (value) => {
      fetchConsumerPoints(value);
      selectConsumer(value);
    },
    [fetchConsumerPoints, selectConsumer]
  );

  const handleSelectPoint = useCallback(
    (value) => {
      const input = { consumerId };

      if (value === ALL) {
        input.noTransport = true;
        input.gasType = FuelType.BIOMETHANE;
      } else {
        input.measurementPointId = value;
      }

      fetchGoAmount(input);
      selectPoint(value);
    },
    [consumerId, fetchGoAmount, selectPoint, eecsGoRows]
  );

  const getAdditionalPointOptions = useCallback(() => {
    return [
      {
        [meteringPointFields.ID]: ALL,
        [meteringPointFields.EICZ_CODE]: t('allPointsLabel'),
      },
    ];
  }, [t]);

  const getPointWarning = useCallback(
    (p, warnings, warnTypes) => {
      if (warnTypes.includes(p.get(meteringPointFields.CONSUMPTION_TYPE))) {
        return false;
      }
      warnTypes.push(p.get(meteringPointFields.CONSUMPTION_TYPE));

      const limits = mapPropertiesToObject(
        classifiersStore.getConsumptionUsageProperties(
          p.get(meteringPointFields.CONSUMPTION_TYPE)
        )
      );

      const from = parseInt(
        limits[classifierConsumptionUsageProperties.START_DAY] || 0,
        10
      );
      const to = parseInt(
        limits[classifierConsumptionUsageProperties.END_DAY] || 0,
        10
      );
      const currentDay = new Date().getDate();

      warnings.push(
        <CancelingAvailabilityWarning
          key={warnTypes.join('-')}
          t={t}
          from={from}
          to={to}
        />
      );
      return currentDay >= from && currentDay <= to;
    },
    [t]
  );

  // If certificates are loaded and empty, return the empty state message
  if (!isCertificatesLoading && (!eecsGoRows || eecsGoRows.size === 0)) {
    return <>{t('groupedEmpty')}</>;
  }

  const warnings = [];
  const warnTypes = [];
  let isCancelAvailable = false;

  if (point && !point.isEmpty()) {
    isCancelAvailable =
      isCancelAvailable || getPointWarning(point, warnings, warnTypes);
  }
  if (pointId === ALL || pointId === ALL_TRANSPORT) {
    isCancelAvailable = true;
  }

  return (
    <Layout className="main-container">
      <h1>{t('title')}</h1>
      {warnings}
      <Spin spinning={isCancellingCertificates}>
        <FormItem layout="vertical" label={t('consumer')}>
          <Row gutter={8}>
            <Col span={20}>
              <SearchLegalEntityConsumers
                value={consumerId}
                onChange={handleSelectConsumer}
              />
            </Col>
            <Col span={4}>
              <Button onClick={goToConsumptionPoint}>
                {t('addPointButton')}
              </Button>
            </Col>
          </Row>
        </FormItem>
        {consumerId && (
          // isCancellingCertificates - to avoid multiple spinners on the same page
          <Spin spinning={isPointsLoading && !isCancellingCertificates}>
            <FormItem layout="vertical" label={t('point')}>
              <Select
                value={pointId}
                valueKey={meteringPointFields.ID}
                labelKey={meteringPointFields.EICZ_CODE}
                onChange={handleSelectPoint}
                options={getAdditionalPointOptions().concat(points.toJS())}
              />
            </FormItem>
          </Spin>
        )}
        {pointId && (
          // isCancellingCertificates - to avoid multiple spinners on the same page
          <Spin
            spinning={
              (isGoLoading || isCertificatesLoading) &&
              !isCancellingCertificates
            }
          >
            <CancelingCertificatesGoAmount
              t={t}
              goAmounts={goAmounts.toJS()}
              periods={periods.toJS()}
              selectPeriods={selectPeriods}
              goAmountTotal={goAmountTotal}
            />
            <h2 className="global-margin-top-24">{t('defineGO')}</h2>
            <CancelEecsGOsTable
              eecsGoRows={eecsGoRows}
              goAmountTotal={goAmountTotal}
              isCanceling
              isCancelAvailable={isCancelAvailable}
              performAction={(eecsGoRows) =>
                cancelCertificates({
                  meteringPointId: pointId,
                  periods: periods.toJS(),
                  currentAccountId: accountId,
                  consumerId,
                  eecsGoRows: eecsGoRows,
                })
              }
            />
          </Spin>
        )}
      </Spin>
    </Layout>
  );
};

CancelingCertificatesEecs.propTypes = {
  pointId: PropTypes.any,
  consumerId: PropTypes.number,
  points: PropTypesImmutable.list.isRequired,
  selectPoint: PropTypes.func.isRequired,
  selectConsumer: PropTypes.func.isRequired,
  fetchConsumerPoints: PropTypes.func.isRequired,
  isPointsLoading: PropTypes.bool.isRequired,
  fetchGoAmount: PropTypes.func.isRequired,
  goAmounts: PropTypesImmutable.list.isRequired,
  eecsGoRows: PropTypesImmutable.list.isRequired,
  point: PropTypesImmutable.map,
  isGoLoading: PropTypes.bool.isRequired,
  isCertificatesLoading: PropTypes.bool.isRequired,
  isCancellingCertificates: PropTypes.bool.isRequired,
  selectPeriods: PropTypes.func.isRequired,
  periods: PropTypesImmutable.list.isRequired,
  cancelCertificates: PropTypes.func.isRequired,
  accountId: PropTypes.string.isRequired,
  goAmountTotal: PropTypes.number.isRequired,
  reset: PropTypes.func.isRequired,
};

const getPointsLoading = getLoadingSelector(selectors.getPoints);
const getGosLoading = getLoadingSelector(selectors.getGoAmount);
const getCertificatesLoading = getLoadingSelector(selectors.getCertificates);
const getIsCancellingCertificatesLoading = getLoadingSelector(
  selectors.getIsCancellingCertificates
);

const mapStateToProps = (state) => ({
  consumerId: selectors.getConsumerId(state),
  pointId: selectors.getPointId(state),
  point: selectors.getSelectedPoint(state),
  points: selectors.getPointsData(state),
  isPointsLoading: getPointsLoading(state),
  isGoLoading: getGosLoading(state),
  isCertificatesLoading: getCertificatesLoading(state),
  isCancellingCertificates: getIsCancellingCertificatesLoading(state),
  goAmounts: selectors.getGoAmountData(state),
  eecsGoRows: getEecsGoRowsForAction(state),
  periods: selectors.getPeriods(state),
  goAmountTotal: selectors.getGoAmountTotal(state),
});

const mapDispatchToProps = {
  selectConsumer: actions.selectConsumer,
  selectPoint: actions.selectPoint,
  fetchConsumerPoints: actions.fetchConsumerPointsForEecs,
  fetchGoAmount: actions.fetchGoAmount,
  selectPeriods: actions.selectPeriods,
  cancelCertificates: actions.cancelCertificatesForEecs,
  reset: actions.reset,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(CancelingCertificatesEecs);
